import { atom } from "jotai";
import { atomWithImmer } from "jotai-immer";

import { ApiError } from "@frontend/lib/http";

import { projectApi } from "../services";
import type { BalanceSheet } from "../types";

export type BalanceSheetState = {
  sheet: BalanceSheet | null;
  price: string;
  hourPrice: string;
  loading: boolean;
  saving: boolean;
  error: string | null;
};

export const initialBalanceSheetState: BalanceSheetState = {
  sheet: null,
  price: "",
  hourPrice: "",
  loading: true,
  saving: false,
  error: null,
};

export const balanceSheetAtom = atomWithImmer<BalanceSheetState>(
  initialBalanceSheetState,
);

export const loadBalanceSheetAtom = atom(
  null,
  async (_get, set, projectId: number): Promise<void> => {
    set(balanceSheetAtom, (state) => {
      state.loading = true;
      state.error = null;
    });

    try {
      const sheet = await projectApi.fetchBalanceSheet(projectId);

      set(balanceSheetAtom, (state) => {
        state.sheet = sheet;
        state.price = sheet.bill?.price ?? "";
        state.hourPrice = sheet.bill?.hourPrice ?? "";
        state.loading = false;
      });
    } catch (error: unknown) {
      set(balanceSheetAtom, (state) => {
        state.loading = false;
        state.error = message(error);
      });
    }
  },
);

export type BalanceSheetAction = "save" | "send" | "close";

/**
 * The three ways a balance sheet ends. All of them carry the figures on
 * screen, and all of them answer the sheet as the API now holds it.
 */
export const applyBalanceSheetAtom = atom(
  null,
  async (get, set, action: BalanceSheetAction): Promise<boolean> => {
    const { sheet, price, hourPrice } = get(balanceSheetAtom);

    if (null === sheet) {
      return false;
    }

    const input = {
      lockVersion: sheet.lockVersion,
      price: blankToNull(price),
      hourPrice: blankToNull(hourPrice),
    };

    set(balanceSheetAtom, (state) => {
      state.saving = true;
      state.error = null;
    });

    try {
      const saved = await run(action, sheet.projectId, input);

      set(balanceSheetAtom, (state) => {
        state.sheet = saved;
        state.price = saved.bill?.price ?? "";
        state.hourPrice = saved.bill?.hourPrice ?? "";
      });

      return true;
    } catch (error: unknown) {
      set(balanceSheetAtom, (state) => {
        state.error = message(error);
      });

      return false;
    } finally {
      set(balanceSheetAtom, (state) => {
        state.saving = false;
      });
    }
  },
);

function run(
  action: BalanceSheetAction,
  projectId: number,
  input: {
    lockVersion: string | null;
    price: string | null;
    hourPrice: string | null;
  },
): Promise<BalanceSheet> {
  if ("send" === action) {
    return projectApi.sendBalanceSheetToBilling(projectId, input);
  }

  if ("close" === action) {
    return projectApi.closeBalanceSheet(projectId, input);
  }

  return projectApi.saveBalanceSheetBill(projectId, input);
}

function blankToNull(value: string): string | null {
  return "" === value.trim()
    ? null
    : value.replace(/\s/gu, "").replace(",", ".");
}

function message(error: unknown): string {
  if (error instanceof ApiError && 409 === error.status) {
    return "conflict";
  }

  return error instanceof Error ? error.message : String(error);
}
